import { Flex } from 'antd';
import { useEffect, useState } from 'react';

import { getUserByMagicLink } from '@/api/user';
import { useRouteServerErrorRegistration } from '@/config/routeServerError';
import { IS_ADMIN_KEY } from '@/constants/roles';
import { DASHBOARD, LOGIN } from '@/constants/routes';
import { useCurrentUser } from '@/hooks/useCurrentUser';
import { useUrlState } from '@/hooks/useUrlState';
import ErrorPage from '@/pages/Error';
import { useAppDispatch } from '@/store/hooks';
import { login } from '@/store/userSlice';
import Loader from '@/UI/Loader';
import {
  getLocalStorageItem,
  removeLocalStorageItem,
  storeAccessToken,
  ADMIN_TOKEN_KEY,
} from '@/utils/localStorage';
import {
  isMagicLinkMfaChallenge,
  resolveNotificationDeepLink,
  type MagicLinkMfaBootstrap,
  type NotificationDeepLinkParams,
} from '@/utils/notificationDeepLink';

function readDeepLinkParams(search: string): NotificationDeepLinkParams {
  const params = new URLSearchParams(search);
  return {
    next: params.get('type'),
    user_id: params.get('user_id'),
    candidate_id: params.get('candidate_id'),
    job_id: params.get('job_id'),
    interview_id: params.get('interview_id'),
    feedback_id: params.get('feedback_id'),
    stage_name: params.get('stage_name'),
  };
}

// Magic-link notification deep links — maps email `type` query params to in-app routes.
const NotificationRoute = () => {
  const [error, setError] = useState(false);
  const [isBootstrapping, setIsBootstrapping] = useState(true);
  const dispatch = useAppDispatch();
  const { history } = useUrlState();
  const { user: sessionUser } = useCurrentUser();

  useRouteServerErrorRegistration(setError);

  /* eslint-disable hireport-design/no-useeffect-api-fetch -- bootstrap: magic-link notification deep-link auth + routing */
  useEffect(() => {
    const params = new URLSearchParams(history.location.search);
    const magic_link = params.get('magic_link');
    const deepLinkParams = readDeepLinkParams(history.location.search);
    const existingToken = getLocalStorageItem('access_token');

    if (!magic_link) {
      history.push(DASHBOARD);
      setIsBootstrapping(false);
      return;
    }

    // Option 1: already authenticated — navigate without re-auth / MFA wipe.
    if (existingToken) {
      if (!sessionUser) {
        // Wait for Redux session hydrate before resolving role-specific deep links.
        return;
      }
      const link = resolveNotificationDeepLink(
        sessionUser.role,
        deepLinkParams,
      );
      history.push(link);
      setIsBootstrapping(false);
      return;
    }

    getUserByMagicLink(magic_link)
      .then((res) => {
        const link = resolveNotificationDeepLink(
          res.user?.role,
          deepLinkParams,
        );

        if (isMagicLinkMfaChallenge(res) && res.mfa_token) {
          const bootstrap: MagicLinkMfaBootstrap = {
            mfaToken: res.mfa_token,
            mfaMethod: res.mfa_method,
            mfaSelectionRequired: Boolean(res.mfa_selection_required),
            userEmail: res.user_email,
            nextPath: link,
          };
          history.push(LOGIN, { magicLinkMfa: bootstrap });
          return;
        }

        const user = res.user;
        const accessToken = res.access_token;
        if (!user || !accessToken) {
          throw new Error('Invalid magic link response');
        }

        storeAccessToken(accessToken);
        dispatch(login({ user, access_token: accessToken }));
        history.push(resolveNotificationDeepLink(user.role, deepLinkParams));
      })
      .catch(() => {
        // Only clear session on definitive failure when we were not already logged in.
        if (!existingToken) {
          removeLocalStorageItem('access_token');
          removeLocalStorageItem(IS_ADMIN_KEY);
          removeLocalStorageItem(ADMIN_TOKEN_KEY);
        }
        history.push(LOGIN);
      })
      .finally(() => setIsBootstrapping(false));
  }, [history, dispatch, sessionUser]);
  /* eslint-enable hireport-design/no-useeffect-api-fetch */

  return (
    <Loader spinning={isBootstrapping && !error}>
      {error ? <ErrorPage /> : <Flex />}
    </Loader>
  );
};

export default NotificationRoute;
